/**
 * ObjectionSettings — pairs of "objection a lead might raise" + "how the AI
 * should respond". Sister panel to FaqSettings.
 *
 * Stored under user_settings.objection_entries (key from SETTING_KEYS.objections).
 * Loaded by the reply pipeline (campaigns/src/reply-handler.ts) and injected
 * into the reply system prompt so the AI uses the user's authored response
 * instead of improvising when the lead pushes back.
 *
 * Distinct from FAQ in two ways:
 *   1. FAQ is for direct questions. Objections are usually stated as concerns
 *      or pushback ("This feels like AI", "We tried this and it didn't work").
 *   2. The reply pipeline scans objections on EVERY inbound reply, not just
 *      ones that look like questions — because objections are often statements.
 *
 * The default seed (Section 0.x of skill.md / 2026-06-03 directive): the
 * "this feels like an AI automated message" objection ships out of the box,
 * with the response Eli/TJ approved. The backend returns this default for
 * users who have never saved this config — so first-time users get the
 * objection without having to click anything.
 */

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  CheckCircle2,
  Shield,
  Plus,
  Trash2,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useApi } from '@/hooks/use-api'
import type { ObjectionConfig, ObjectionEntry } from '@dealcloser/shared'
import { DEFAULT_OBJECTIONS } from '@dealcloser/shared'

const OBJECTION_QUERY_KEY = ['settings', 'objections'] as const

const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px] resize-y'

const MAX_OBJECTION_LEN = 500
const MAX_RESPONSE_LEN = 4000
const MAX_ENTRIES = 100

function newEntryId(): string {
  return `obj_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export default function ObjectionSettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [entries, setEntries] = useState<ObjectionEntry[]>([])
  const [savedSuccess, setSavedSuccess] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: OBJECTION_QUERY_KEY,
    queryFn: async (): Promise<ObjectionConfig> => {
      const res = await apiFetch<ObjectionConfig>('/api/settings/objections')
      if (!res.success || !res.data) return DEFAULT_OBJECTIONS
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (data) {
      setEntries(
        data.entries.map((e) => ({
          id: e.id,
          objection: e.objection,
          response: e.response,
        })),
      )
    }
  }, [data])

  const isDirty = useMemo(() => {
    if (!data) return false
    return JSON.stringify(entries) !== JSON.stringify(data.entries)
  }, [data, entries])

  const saveMutation = useMutation({
    mutationFn: async (next: readonly ObjectionEntry[]): Promise<void> => {
      const cleaned = next
        .map((e) => ({
          id: e.id,
          objection: e.objection.trim(),
          response: e.response.trim(),
        }))
        .filter((e) => e.objection.length > 0 && e.response.length > 0)

      const res = await apiFetch<ObjectionConfig>('/api/settings/objections', {
        method: 'PUT',
        body: { entries: cleaned },
      })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OBJECTION_QUERY_KEY })
      setSavedSuccess(true)
      setValidationError(null)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  function handleAdd(): void {
    if (entries.length >= MAX_ENTRIES) {
      setValidationError(`You've reached the ${MAX_ENTRIES}-entry maximum. Remove an entry to add another.`)
      return
    }
    setValidationError(null)
    setEntries((prev) => [...prev, { id: newEntryId(), objection: '', response: '' }])
  }

  function handleUpdate(id: string, field: 'objection' | 'response', value: string): void {
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    )
  }

  function handleDelete(id: string): void {
    setEntries((prev) => prev.filter((e) => e.id !== id))
  }

  function handleSave(): void {
    setValidationError(null)
    for (const e of entries) {
      const o = e.objection.trim()
      const r = e.response.trim()
      if (o.length > 0 && r.length === 0) {
        setValidationError('One of your entries has an objection but no response. Fill it in or remove the entry.')
        return
      }
      if (o.length === 0 && r.length > 0) {
        setValidationError('One of your entries has a response but no objection. Fill it in or remove the entry.')
        return
      }
      if (e.objection.length > MAX_OBJECTION_LEN) {
        setValidationError(`An objection exceeds the ${MAX_OBJECTION_LEN}-character limit. Shorten it before saving.`)
        return
      }
      if (e.response.length > MAX_RESPONSE_LEN) {
        setValidationError(`A response exceeds the ${MAX_RESPONSE_LEN}-character limit. Shorten it before saving.`)
        return
      }
    }
    saveMutation.mutate(entries)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
          Objections — counters the AI uses when leads push back
        </CardTitle>
        <CardDescription>
          When a lead raises a concern instead of asking a question — "this feels like AI",
          "we already use X", "not the right time" — the AI checks this list and uses your
          authored counter. <strong>Used on every reply</strong>, not just questions. We pre-seed
          the "feels like AI" objection because it's the single most common pushback we see.
          Edit it, add more, or remove it entirely.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading objections…
          </div>
        ) : entries.length === 0 ? (
          <EmptyState onAdd={handleAdd} />
        ) : (
          <ul className="flex flex-col gap-3">
            {entries.map((entry, idx) => (
              <li key={entry.id}>
                <EntryCard
                  index={idx}
                  entry={entry}
                  disabled={saveMutation.isPending}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              </li>
            ))}
          </ul>
        )}

        {entries.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={saveMutation.isPending || entries.length >= MAX_ENTRIES}
            className="self-start"
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add another objection
          </Button>
        )}

        {validationError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        {saveMutation.error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save'}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            {isDirty ? (
              <span className="text-amber-400">You have unsaved changes.</span>
            ) : entries.length > 0 ? (
              <>
                {entries.filter((e) => e.objection.trim() && e.response.trim()).length} active entr
                {entries.filter((e) => e.objection.trim() && e.response.trim()).length === 1 ? 'y' : 'ies'}
              </>
            ) : (
              'No objections yet.'
            )}
          </p>
          <div className="flex items-center gap-2">
            {savedSuccess && (
              <span className="flex items-center gap-1 text-xs text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
            <Button onClick={handleSave} disabled={saveMutation.isPending || !isDirty} size="sm">
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save Objections'
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function EmptyState({ onAdd }: { readonly onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] py-8 text-center">
      <Shield className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-medium">No objections yet</p>
      <p className="max-w-md text-xs text-muted-foreground">
        Add the concerns leads typically raise — "this feels automated", "we already use X",
        "we don't have budget" — and the AI will counter them with the response you write here.
      </p>
      <Button size="sm" onClick={onAdd}>
        <Plus className="mr-1 h-3.5 w-3.5" />
        Add your first objection
      </Button>
    </div>
  )
}

function EntryCard({
  index,
  entry,
  disabled,
  onUpdate,
  onDelete,
}: {
  readonly index: number
  readonly entry: ObjectionEntry
  readonly disabled: boolean
  readonly onUpdate: (id: string, field: 'objection' | 'response', value: string) => void
  readonly onDelete: (id: string) => void
}) {
  const objectionOverLimit = entry.objection.length > MAX_OBJECTION_LEN
  const responseOverLimit = entry.response.length > MAX_RESPONSE_LEN
  return (
    <div className="flex flex-col gap-3 rounded-md border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="rounded bg-[rgba(28,63,255,0.12)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-cobalt-bright)]">
          O{index + 1}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(entry.id)}
          disabled={disabled}
          aria-label={`Delete objection ${index + 1}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`obj-q-${entry.id}`} className="text-xs">Objection the lead might raise</Label>
        <Input
          id={`obj-q-${entry.id}`}
          value={entry.objection}
          onChange={(e) => onUpdate(entry.id, 'objection', e.target.value)}
          placeholder='e.g. "This feels like an AI automated message to me."'
          maxLength={MAX_OBJECTION_LEN + 100}
          disabled={disabled}
          aria-invalid={objectionOverLimit}
        />
        <div className="flex items-center justify-between">
          <span className={`text-[10px] ${objectionOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
            {entry.objection.length}/{MAX_OBJECTION_LEN}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`obj-a-${entry.id}`} className="text-xs">How the AI should respond</Label>
        <textarea
          id={`obj-a-${entry.id}`}
          className={TEXTAREA_CLASS}
          value={entry.response}
          onChange={(e) => onUpdate(entry.id, 'response', e.target.value)}
          placeholder='e.g. "I promise you the amount of sales closed and time saved far outweigh the amount of people who can detect this is AI."'
          maxLength={MAX_RESPONSE_LEN + 200}
          disabled={disabled}
          aria-invalid={responseOverLimit}
        />
        <div className="flex items-center justify-between">
          <span className={`text-[10px] ${responseOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
            {entry.response.length}/{MAX_RESPONSE_LEN}
          </span>
        </div>
      </div>
    </div>
  )
}
